import styled, { css, type Interpolation } from 'styled-components';

import PrimaryText from 'components/core/typography/PrimaryText';
import { CloseIcon } from 'components/ui/icons';
import { Clickable } from 'components/ui/shared/Buttons/Clickable';
import { formatTitleString } from 'utils/formatUtils';

import { BODY_TEXT } from 'styles/color';
import { FONT_SIZE_16, NEUTRAL_0, NEUTRAL_100, NEUTRAL_800, SPACE_400 } from 'styles/tokens';

export const DialogContentContainer = styled.div`
  background: ${NEUTRAL_0};
  max-height: 100%;
  overflow: auto;
  position: relative;
`;

export const DialogBodyContentContainer = styled.div`
  max-height: 50vh;
  overflow: auto;

  &:only-child {
    padding-top: ${SPACE_400};
  }
`;

export const DialogBodyCSS = css`
  align-items: center;
  background: ${NEUTRAL_0};
  display: flex;
  flex-direction: column;
  min-width: 272px;
  padding: 0 ${SPACE_400} ${SPACE_400} ${SPACE_400};
`;

const DialogHeaderSection = styled.section<{ $containerStyleOverride?: Interpolation<object>; $showDivider?: boolean }>`
  align-items: center;
  border-bottom: ${({ $showDivider }) => ($showDivider ? `1px solid ${NEUTRAL_100}` : 'none')};
  color: ${BODY_TEXT};
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: ${SPACE_400};
  width: 100%;
  ${({ $containerStyleOverride }) => $containerStyleOverride};
`;

const DialogButton = styled(Clickable)`
  height: 53px;
  position: absolute;
  right: 0;
  top: 0;
  width: 53px;
`;

const DialogHeaderTitle = styled(PrimaryText)<{ $titleStyleOverride?: Interpolation<object> }>`
  color: ${NEUTRAL_800};
  font-size: ${FONT_SIZE_16};
  pointer-events: none;
  ${({ $titleStyleOverride }) => $titleStyleOverride};
`;

interface Props {
  /**
   * Custom styles to override the default container styling
   */
  containerStyleOverride?: Interpolation<object>;
  /**
   * An optional callback for 'X' button in dialog header, if left null 'X' will be hidden
   */
  onClose?: () => void;
  /**
   * Whether or not a divider should be shown at the bottom of the header. Defaults to false
   */
  showDivider?: boolean;
  /**
   * Header title
   */
  title: string;
  /**
   * Custom styles to override the default title styling
   */
  titleStyleOverride?: Interpolation<object>;
}

export const DialogHeader = ({
  containerStyleOverride,
  onClose,
  showDivider = false,
  title,
  titleStyleOverride,
}: Props) => (
  <DialogHeaderSection $containerStyleOverride={containerStyleOverride} $showDivider={showDivider}>
    <DialogHeaderTitle $titleStyleOverride={titleStyleOverride}>{formatTitleString(title)}</DialogHeaderTitle>
    {onClose ? (
      <DialogButton onClick={onClose}>
        <CloseIcon />
      </DialogButton>
    ) : null}
  </DialogHeaderSection>
);
